import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useRef,
  useState,
} from "react";
import type {
  Notification,
  NotificationVariant,
} from "services/notification/types";
import {NotificationContext} from "./NotificationContext";

const generateId = (): string =>
  `notification-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

export const NotificationProvider: FC<PropsWithChildren> = ({children}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const timers = useRef<Record<string, number>>({});

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const addNotification = useCallback(
    (message: string, variant: NotificationVariant) => {
      const id = generateId();

      setNotifications(prev => [
        ...prev,
        {
          id,
          message,
          variant,
          timestamp: Date.now(),
        },
      ]);

      timers.current[id] = window.setTimeout(() => {
        removeNotification(id);
      }, 5000);

      return id;
    },
    [removeNotification],
  );

  return (
    <NotificationContext.Provider
      value={{notifications, addNotification, removeNotification}}>
      {children}
    </NotificationContext.Provider>
  );
};
